import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useI18n, supportedLanguages } from "@/i18n";

const SECTION_IDS = [
  { key: "how", id: "como-funciona" },
  { key: "projects", id: "proyectos" },
  { key: "about", id: "nosotros" },
  { key: "contact", id: "contacto" },
];

function LangSwitcher() {
  const { lang } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();

  const switchTo = (target) => {
    if (target === lang) return;
    let path = location.pathname;
    const parts = path.split("/").filter(Boolean);
    if (parts.length && supportedLanguages.includes(parts[0])) {
      parts[0] = target;
    } else {
      parts.unshift(target);
    }
    const nextPath = "/" + parts.join("/");
    navigate(nextPath + location.hash, { state: { langSwitch: true } });
  };

  return (
    <div className="lang-switcher" role="group" aria-label="Language">
      <Globe size={16} strokeWidth={1.8} />
      {supportedLanguages.map((code) => (
        <button
          key={code}
          type="button"
          className={`lang-btn ${lang === code ? "is-active" : ""}`}
          onClick={() => switchTo(code)}
          aria-pressed={lang === code}
          data-testid={`lang-${code}`}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function Header() {
  const { t, lang } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname, location.hash]);

  const base = `/${lang}`;

  const goToSection = (id) => (e) => {
    e.preventDefault();
    const isOnLang = location.pathname === base || location.pathname === base + "/" || location.pathname === "/";
    if (!isOnLang) {
      navigate(base + "#" + id);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      else window.location.hash = id;
    }
    setOpen(false);
  };

  return (
    <header className={`site-header ${scrolled ? "is-scrolled" : ""}`} data-testid="site-header">
      <div className="header-inner">
        <Link to={base} className="brand" data-testid="brand-link" aria-label="Mirexilava">
          <span className="brand-mark" aria-hidden="true">
            <svg viewBox="0 0 32 32" width="28" height="28" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="11" width="4" height="16" rx="1" fill="currentColor"/>
              <rect x="9" y="7" width="5" height="20" rx="1" fill="currentColor"/>
              <rect x="16" y="13" width="4" height="14" rx="1" fill="currentColor"/>
              <rect x="22" y="10" width="4" height="17" rx="1" fill="currentColor"/>
              <rect x="2" y="27" width="25" height="2" fill="currentColor"/>
            </svg>
          </span>
          <span className="brand-text">Mirexilava</span>
        </Link>

        <nav className="nav-desktop" aria-label="Primary">
          {SECTION_IDS.map((s) => (
            <a key={s.key} href={`${base}#${s.id}`} onClick={goToSection(s.id)} className="nav-link" data-testid={`nav-${s.key}`}>
              {t(`nav.${s.key}`)}
            </a>
          ))}
          <LangSwitcher />
          <a href={`${base}#contacto`} onClick={goToSection("contacto")} className="btn btn-primary btn-sm" data-testid="nav-cta">
            {t("cta.contactUs")}
          </a>
        </nav>

        <button
          className="nav-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
          data-testid="nav-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <div className={`nav-mobile ${open ? "is-open" : ""}`} role="dialog" aria-hidden={!open}>
        <div className="nav-mobile-inner">
          {SECTION_IDS.map((s) => (
            <a key={s.key} href={`${base}#${s.id}`} onClick={goToSection(s.id)} className="nav-mobile-link" data-testid={`nav-mobile-${s.key}`}>
              {t(`nav.${s.key}`)}
            </a>
          ))}
          <div className="nav-mobile-actions">
            <LangSwitcher />
            <a href={`${base}#contacto`} onClick={goToSection("contacto")} className="btn btn-primary" data-testid="nav-mobile-cta">
              {t("cta.contactUs")}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
